import {
    type FC,
    Fragment,
    type MutableRefObject,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Link } from "react-router-dom";
import FontAwesomeLight from "../../../../../app/Utilities/Platform/FontAwesome/FontAwesomeLight";
import { type NavigationType } from "../Navigation";
import { type SearchResult, useNavigationSearch } from "./NavigationSearch";

interface Props {
    search: string;
    navigation: NavigationType;
    open: boolean;
    setOpen: (open: boolean) => void;
}

/**
 * Creates the url for a search result
 * @param result
 */
function createUrl(result: SearchResult): string {
    let group: string;
    let article: string;
    let hash: string;

    switch(result.parents.length) {
        case 0:
            group = `/${result.name}`;
            article = "";
            hash = "";
            break;
        case 1:
            group = `/${result.parents[0]}`;
            article = `/${result.name}`;
            hash = "";
            break;
        default:
            group = `/${result.parents[0]}`;
            article = `/${result.parents[1]}`;
            hash = `#${result.name}`;
            break;
    }

    return `/documentation${group}${article}${hash}`.replace(/ /g, "-");
}

function handleHighlight(
    value: number,
    relative: boolean,
    refMap: MutableRefObject<Record<number, HTMLElement>>,
    lengthRef: MutableRefObject<number>
) {
    return (prevState: null | number): number | null => {
        let newValue: number;

        if(prevState === null) {
            newValue = (
                relative ?
                    (
                        value < 0 ?
                            lengthRef.current - 1 :
                            0
                    ) :
                    Math.min(lengthRef.current - 1, Math.max(0, value))
            );
        } else {
            newValue = Math.min(
                lengthRef.current - 1,
                Math.max(
                    0,
                    relative ? prevState + value : value
                )
            );
        }

        if(prevState === newValue) {
            return newValue;
        }

        refMap.current[newValue]?.scrollIntoView({
            block: "nearest",
        });

        return newValue;
    };
}

export const NavigationSearchResults: FC<Props> = ({
    search,
    navigation,
    open,
    setOpen,
}) => {
    const refMap = useRef<Record<number, HTMLElement>>({});

    const resultCountRef = useRef(0);
    const highlightRef = useRef<number | null>(null);

    const [ highlight, setHighlight ] = useState<number | null>(null);

    highlightRef.current = highlight;

    const [ onSearch, searchResults, rawResults ] = useNavigationSearch(navigation);

    const results = useMemo(() => {
        if(!search.trim()) {
            return rawResults;
        }

        return searchResults;
    }, [ search, searchResults, rawResults ]);

    useEffect(() => {
        setHighlight(null);
        onSearch(search);
    }, [ search, onSearch ]);

    resultCountRef.current = results.length;

    useEffect(() => {
        if(!open) {
            setHighlight(null);

            return;
        }

        const listener = (e: KeyboardEvent) => {
            if(e.key === "ArrowUp") {
                setHighlight(handleHighlight(-1, true, refMap, resultCountRef));
            } else if(e.key === "ArrowDown") {
                setHighlight(handleHighlight(1, true, refMap, resultCountRef));
            } else if(e.key === "PageUp") {
                setHighlight(handleHighlight(-10, true, refMap, resultCountRef));
            } else if(e.key === "PageDown") {
                setHighlight(handleHighlight(10, true, refMap, resultCountRef));
            } else if(e.key === "Home") {
                setHighlight(handleHighlight(0, false, refMap, resultCountRef));
            } else if(e.key === "End") {
                setHighlight(handleHighlight(resultCountRef.current, false, refMap, resultCountRef));
            } else if(e.key === "Enter") {
                if(highlightRef.current === null) {
                    return;
                }

                refMap.current[highlightRef.current]?.click();
            }
        };

        document.addEventListener("keydown", listener);

        return () => {
            document.removeEventListener("keydown", listener);
        };
    }, [ open ]);

    return (
        <>
            {results.map((result, index) => {
                const key = [ ...result.parents, result.name ].join("/");

                const isHighlight = highlight === index;

                return (
                    <Link
                        ref={el => {
                            if(!el) {
                                delete refMap.current[index];
                            } else {
                                refMap.current[index] = el;
                            }
                        }}
                        onClick={() => {
                            setOpen(false);
                        }}
                        key={key}
                        className={
                            "block py-4 px-6 text-base no-underline text-black hover:bg-primary/40" + (
                                isHighlight ?
                                    " bg-primary/20" :
                                    ""
                            )
                        }
                        to={createUrl(result)}
                    >
                        {result.parents.length > 0 && (
                            <div className="text-xs text-neutral-500">
                                {result.parents.map((parent, index) => (
                                    <Fragment key={index}>
                                        {index > 0 && (
                                            <span>
                                                <FontAwesomeLight
                                                    icon="angle-right"
                                                    className="mr-0!"
                                                />
                                            </span>
                                        )}
                                        {parent}
                                    </Fragment>
                                ))}
                            </div>
                        )}
                        {result.name}
                    </Link>
                );
            })}
            {results.length === 0 && (
                <div className="w-full p-8 text-neutral-500 box-border text-center">
                    No search results found...
                </div>
            )}
        </>
    );
};
